import React, { useState, useEffect } from "react";
import { FileText, Plus, Edit, X, Upload, Download, Eye, CheckCircle2 } from "lucide-react";
import api, { BASE } from "../api";
import { uploadUrl } from "../uploadUrl";
import { K } from "../theme";
import { Cd, Bg, Bt } from "../components";
import { FormModal } from "../components/FormModal";
import { notify, confirmDialog } from "../notify";

// window.open() on a PDF/image just navigates the new tab to it, which
// browsers render inline instead of saving — there's no way to force a
// save from a plain link/navigation. A temporary <a download> element does
// force it (uploads are served as plain static files, no auth needed).
const downloadFile = (url, filename) => {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "";
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const DocumentsPage = ({ docData, role, onRefresh, can = () => true }) => {
  const [showForm, setShowForm] = useState(null);
  const [editVals, setEditVals] = useState({});
  const [uploadingId, setUploadingId] = useState(null);
  const [deactivatingId, setDeactivatingId] = useState(null);
  const [ackingId, setAckingId] = useState(null);
  // Matches the backend: only admin/hr_head can create, upload, edit, or
  // deactivate a policy (server/routes/documents.js requireRole(...)).
  // Everyone can still view/download — this page is read-only for anyone else.

  const dd = docData || {};
  const policies = dd.policies || [];
  const canManage = can('documents', null, 'add_policy', 'edit') || can('documents', null, 'edit_policy', 'edit');

  // Companies and centres a policy can be aimed at (loaded once, only for people who manage policies).
  const [companyOpts, setCompanyOpts] = useState([]);
  const [officeOpts, setOfficeOpts] = useState([]);
  useEffect(() => {
    if (!canManage) return;
    api.get("/api/companies?active=true").then(d => setCompanyOpts((Array.isArray(d) ? d : d?.data || []).map(c => ({ v: String(c.id), l: c.name })))).catch(() => {});
    api.get("/api/offices").then(d => setOfficeOpts((Array.isArray(d) ? d : d?.data || []).map(o => ({ v: String(o.id), l: o.city ? `${o.name} (${o.city})` : o.name })))).catch(() => {});
    // eslint-disable-next-line
  }, [canManage]);
  const audienceFields = [
    { key: "company_ids", label: "Company — who it is for (leave empty for all companies)", type: "multi-select", options: companyOpts, placeholder: "All companies" },
    { key: "office_ids", label: "Centres — who it is for (leave empty for all centres)", type: "multi-select", options: officeOpts, placeholder: "All centres" },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-[14px]">
        <h1 className="m-0 text-2xl font-extrabold text-t0">Company Policies</h1>
        {can('documents', null, 'add_policy', 'edit') && <Bt icon={Plus} s="sm" onClick={() => setShowForm("policy")}>Add Policy</Bt>}
      </div>
      <div className="text-[14px] text-t2 mb-[14px]">Upload and manage policy documents. Each policy can be for all companies and centres, or only the ones you pick; employees see only the policies meant for their company and centre.</div>

      {policies.map(p => (
        <Cd key={p.id} style={{ marginBottom: 8 }}>
          <div className="flex justify-between items-center flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${p.file ? "bg-gb" : "bg-ab"}`}>
                <FileText size={18} color={p.file ? K.g : K.a} />
              </div>
              <div className="min-w-0">
                <div className="text-[15px] font-semibold text-t0 truncate">{p.t}</div>
                <div className="flex items-center gap-[6px] mt-[3px] flex-wrap">
                  <Bg c={p.cat === "Compliance" ? K.r : p.cat === "Leave" ? K.bl : p.cat === "HR" ? K.pu : p.cat === "Finance" ? K.g : p.cat === "IT" ? K.tl : p.cat === "Attendance" ? K.cy : K.t3} sm>{p.cat}</Bg>
                  {p.file ? <Bg c={K.g} sm dot>Document Uploaded</Bg> : <Bg c={K.a} sm>No Document</Bg>}
                  {canManage && <Bg c={K.bl} sm>For: {p.aud}</Bg>}
                  {p.file && p.reqAck && (p.myAck
                    ? <Bg c={K.g} sm>Acknowledged</Bg>
                    : <Bg c={p.ackOverdue ? K.r : K.a} sm>{p.ackOverdue ? "Acknowledgment Overdue" : "Acknowledgment Pending"}</Bg>)}
                </div>
              </div>
            </div>
            <div className="flex gap-1 flex-wrap flex-shrink-0">
              {p.file && <Bt v="secondary" s="sm" icon={Eye} onClick={() => window.open(uploadUrl(p.file), "_blank")}>View</Bt>}
              {p.file && <Bt v="success" s="sm" icon={Download} onClick={() => downloadFile(uploadUrl(p.file), p.t + "." + (p.file.split(".").pop() || ""))}>Download</Bt>}
              {p.file && p.reqAck && !p.myAck && (
                <Bt v="primary" s="sm" icon={CheckCircle2} disabled={ackingId === p.id} onClick={async () => {
                  setAckingId(p.id);
                  try {
                    await api.put("/api/documents/policies/" + p.id + "/acknowledge");
                    onRefresh?.();
                    notify("Policy acknowledged", { title: "Saved", variant: "success" });
                  } catch (e) { notify(e.message || "Failed to acknowledge", { title: "Error", variant: "error" }); }
                  finally { setAckingId(null); }
                }}>{ackingId === p.id ? "Acknowledging..." : "Acknowledge"}</Bt>
              )}
              {can('documents', null, 'upload_policy_file', 'edit') && <label className={`inline-flex items-center gap-[5px] px-[10px] py-[4px] rounded-[10px] text-[14px] font-semibold cursor-pointer border ${p.file ? "bg-bg2 text-t2 border-b1" : "bg-pb text-p border-pb"}`}>
                <Upload size={12} />{uploadingId === p.id ? "Uploading..." : p.file ? "Replace" : "Upload Document"}
                <input type="file" accept=".pdf,.doc,.docx,.png,.jpg" className="hidden" disabled={uploadingId === p.id} onChange={async (e) => {
                  const file = e.target.files[0]; if (!file) return;
                  const fd = new FormData(); fd.append("file", file);
                  setUploadingId(p.id);
                  try {
                    await fetch(BASE + "/api/documents/policies/" + p.id + "/upload", { method: "POST", headers: { "Authorization": "Bearer " + localStorage.getItem("hrms_token") }, body: fd });
                    onRefresh?.();
                    notify("Document uploaded successfully", { title: "Saved", variant: "success" });
                  } catch (err) { notify(err.message || "Upload failed", { title: "Error", variant: "error" }); }
                  finally { setUploadingId(null); }
                }} />
              </label>}
              {can('documents', null, 'edit_policy', 'edit') && <Bt v="ghost" s="sm" icon={Edit} onClick={() => { setShowForm("edit_policy"); setEditVals({ _polId: p.id, title: p.t, category: p.cat, company_ids: (p.companyIds || []).map(String), office_ids: (p.officeIds || []).map(String) }); }}>Edit</Bt>}
              {can('documents', null, 'deactivate_policy', 'edit') && <Bt v="ghost" s="sm" icon={X} disabled={deactivatingId === p.id} onClick={async () => {
                if (!(await confirmDialog("Deactivate '" + p.t + "'?"))) return;
                setDeactivatingId(p.id);
                try {
                  await api.put("/api/documents/policies/" + p.id, { is_active: false });
                  onRefresh?.();
                  notify("Policy deactivated", { title: "Saved", variant: "success" });
                } catch (e) { notify(e.message || "Failed to deactivate policy", { title: "Error", variant: "error" }); }
                finally { setDeactivatingId(null); }
              }}>{deactivatingId === p.id ? "Deactivating..." : "Deactivate"}</Bt>}
            </div>
          </div>
        </Cd>
      ))}

      {policies.length === 0 && (
        <Cd>
          <div className="text-center p-10 text-t3">
            <FileText size={32} color={K.t3} className="mb-2" />
            <div className="text-[15px]">No company policies yet</div>
            <div className="text-[14px] mt-1">Click "Add Policy" to create your first policy document</div>
          </div>
        </Cd>
      )}

      {showForm === "policy" && (
        <FormModal title="Add Company Policy" fields={[
          { key: "title", label: "Policy Name", placeholder: "Leave Policy" },
          { key: "category", label: "Category", type: "select", options: [{ v: "General", l: "General" }, { v: "Leave", l: "Leave" }, { v: "Attendance", l: "Attendance" }, { v: "Compliance", l: "Compliance" }, { v: "Finance", l: "Finance" }, { v: "IT", l: "IT" }, { v: "HR", l: "HR" }] },
          ...audienceFields,
        ]} onSubmit={async v => {
          try { await api.post("/api/documents/policies", { ...v, company_ids: v.company_ids || [], office_ids: v.office_ids || [] }); onRefresh?.(); notify("Policy added successfully", { title: "Saved", variant: "success" }); }
          catch (e) { notify(e.message || "Failed to add policy", { title: "Error", variant: "error" }); throw e; }
        }}
        onClose={() => setShowForm(null)} />
      )}

      {showForm === "edit_policy" && (
        <FormModal title="Edit Policy" fields={[
          { key: "title", label: "Policy Name", placeholder: editVals.title || "" },
          { key: "category", label: "Category", type: "select", options: [{ v: "General", l: "General" }, { v: "Leave", l: "Leave" }, { v: "Attendance", l: "Attendance" }, { v: "Compliance", l: "Compliance" }, { v: "Finance", l: "Finance" }, { v: "IT", l: "IT" }, { v: "HR", l: "HR" }] },
          ...audienceFields,
        ]} initialValues={{ company_ids: editVals.company_ids || [], office_ids: editVals.office_ids || [] }} onSubmit={async v => {
          try {
            await api.put("/api/documents/policies/" + editVals._polId, { title: v.title || editVals.title, category: v.category || editVals.category, company_ids: v.company_ids || [], office_ids: v.office_ids || [] });
            onRefresh?.(); setEditVals({});
            notify("Policy updated successfully", { title: "Saved", variant: "success" });
          } catch (e) { notify(e.message || "Failed to update policy", { title: "Error", variant: "error" }); throw e; }
        }} onClose={() => { setShowForm(null); setEditVals({}); }} />
      )}
    </div>
  );
};
